// Receipts tab body (slice 4e). Mirrors
// `ManufacturingInventoryChangelogBody` so the two read-side
// inventory tabs feel consistent. Owns:
//   - a paginated list of recent receipts (vendor, date, totals, file
//     pointer, delete button).
//   - an inline upload form (`ManufacturingReceiptUploadForm`,
//     extracted sibling) that runs the sign + upload + commit dance
//     through `useManufacturingReceipts.upload`.
//
// Plant picker is intentionally omitted in this slice -- the server
// resolves to the org's default plant when plant_id is missing
// (matches the receive-lines convention of "ambient plant"). A picker
// can be added when there's a shared scope across the Inventory panel.
import { useEffect, useMemo, useState } from "react";
import { mfgXsGhostBtnClass, mfgXsInputClass } from "@/components/manufacturing/manufacturingSurfaceClasses";
import type {
  MfgInventoryReceipt,
  MfgReceiptAttentionItem,
} from "@/lib/manufacturing/types";
import {
  ManufacturingReceiptUploadForm,
  type ReceiptUploadDraft,
} from "./ManufacturingReceiptUploadForm";
import { useManufacturingReceipts } from "./useManufacturingReceipts";
import { useManufacturingMaterials } from "./useManufacturingMaterials";
import { useManufacturingReceiptsAttention } from "./useManufacturingReceiptsAttention";
import { ManufacturingReceiptExtractionModal } from "./ManufacturingReceiptExtractionModal";
import { ManufacturingReceiptStatusChip } from "./ManufacturingReceiptStatusChip";
import { InlineHelp } from "@/components/common/InlineHelp";

const PAGE_SIZES = [10, 20, 50] as const;
type PageSize = (typeof PAGE_SIZES)[number];
const DEFAULT_PAGE_SIZE: PageSize = 20;

function todayIso(): string {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function parseAmount(raw: string): number | null {
  if (raw.trim() === "") return 0;
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 0) return null;
  return n;
}

function emptyDraft(): ReceiptUploadDraft {
  return {
    vendor: "",
    date: todayIso(),
    total: "",
    shipping: "",
    tax: "",
    notes: "",
  };
}

type Props = {
  enabled: boolean;
};

export function ManufacturingInventoryReceiptsBody({ enabled }: Props) {
  const [pageSize, setPageSize] = useState<PageSize>(DEFAULT_PAGE_SIZE);
  const [page, setPage] = useState(0);

  useEffect(() => {
    setPage(0);
  }, [pageSize]);

  const filters = useMemo(
    () => ({ limit: pageSize, offset: page * pageSize }),
    [pageSize, page]
  );

  const {
    receipts,
    total,
    loading,
    working,
    error,
    refresh,
    upload,
    remove,
  } = useManufacturingReceipts({ enabled, filters });

  const [draft, setDraft] = useState<ReceiptUploadDraft>(emptyDraft);
  const [file, setFile] = useState<File | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);
  const [localToast, setLocalToast] = useState<string | null>(null);
  const [reviewing, setReviewing] = useState<MfgInventoryReceipt | null>(null);
  const { materials, refresh: refreshMaterials } = useManufacturingMaterials({ enabled });
  const attention = useManufacturingReceiptsAttention({ enabled });

  const pageCount = total > 0 ? Math.ceil(total / pageSize) : 1;
  const safePage = Math.min(page, Math.max(0, pageCount - 1));
  const rangeStart = total === 0 ? 0 : safePage * pageSize + 1;
  const rangeEnd = Math.min((safePage + 1) * pageSize, total);
  const canPrev = safePage > 0;
  const canNext = total > 0 && safePage < pageCount - 1;

  async function submitUpload() {
    setLocalError(null);
    setLocalToast(null);
    if (!file) {
      setLocalError("Pick a receipt file (PDF or image) first.");
      return;
    }
    if (!draft.date) {
      setLocalError("Receipt date is required.");
      return;
    }
    const total = parseAmount(draft.total);
    const shipping = parseAmount(draft.shipping);
    const tax = parseAmount(draft.tax);
    if (total === null || shipping === null || tax === null) {
      setLocalError("Costs must be non-negative numbers.");
      return;
    }
    const r = await upload({
      file,
      metadata: {
        vendor_name: draft.vendor.trim() || undefined,
        receipt_date: draft.date,
        total_cost: total,
        shipping_cost: shipping,
        tax_amount: tax,
        notes: draft.notes.trim() || undefined,
      },
    });
    if (!r.ok) {
      setLocalError(r.error);
      return;
    }
    setLocalToast("Receipt uploaded.");
    setDraft(emptyDraft());
    setFile(null);
  }

  async function handleDelete(receiptId: string) {
    setLocalError(null);
    setLocalToast(null);
    const ok = window.confirm("Delete this receipt? The file will be removed too.");
    if (!ok) return;
    const r = await remove(receiptId);
    if (!r.ok) setLocalError(r.error);
    else setLocalToast("Receipt deleted.");
  }

  return (
    <div className="space-y-3">
      <ManufacturingReceiptUploadForm
        draft={draft}
        setDraft={setDraft}
        file={file}
        setFile={setFile}
        working={working}
        onSubmit={submitUpload}
      />

      {localError ? (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-900 dark:border-red-800 dark:bg-red-950/35 dark:text-red-100">
          {localError}
        </div>
      ) : null}
      {localToast ? (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950/35 dark:text-emerald-100">
          {localToast}
        </div>
      ) : null}

      <div className="flex flex-wrap items-center gap-2">
        <label className="flex items-center gap-1 text-[11px] text-gray-600 dark:text-zinc-400">
          Per page
          <select
            className={mfgXsInputClass}
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value) as PageSize)}
          >
            {PAGE_SIZES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          className={mfgXsGhostBtnClass}
          onClick={() => void refresh()}
          disabled={working}
        >
          {working ? "Refreshing…" : "Refresh"}
        </button>
        <InlineHelp
          label="What the per-row chips mean"
          dismissKey="mfg.receipts.rowChips"
        >
          Each row carries a status chip:
          <ul className="mt-1.5 ml-3 list-disc space-y-0.5">
            <li><strong>not OCR'd</strong> — upload landed; click Review to extract.</li>
            <li><strong>0 of N / X of N posted</strong> — OCR pulled N lines; some still need posting.</li>
            <li><strong>all posted</strong> — every extracted line has a cost entry.</li>
            <li><strong>dismissed</strong> — silenced from the attention counter via the Review modal.</li>
          </ul>
        </InlineHelp>
        <p className="ml-auto text-[11px] text-gray-600 dark:text-zinc-400">
          {total === 0 ? (
            "No receipts yet"
          ) : (
            <>
              Showing{" "}
              <span className="tabular-nums font-medium text-gray-800 dark:text-zinc-200">{rangeStart}</span>
              –<span className="tabular-nums font-medium text-gray-800 dark:text-zinc-200">{rangeEnd}</span>{" "}
              of <span className="tabular-nums font-medium text-gray-800 dark:text-zinc-200">{total}</span>
            </>
          )}
        </p>
      </div>

      {error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-900 dark:border-red-800 dark:bg-red-950/35 dark:text-red-100">
          {error}
        </div>
      ) : null}

      {loading ? (
        <div className="text-sm text-gray-500 dark:text-zinc-400">Loading receipts…</div>
      ) : receipts.length === 0 && !error ? (
        <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50 px-3 py-3 text-xs text-gray-600 dark:border-zinc-700 dark:bg-zinc-800/40 dark:text-zinc-400">
          No receipts uploaded yet. Use the form above to file one.
        </div>
      ) : (
        <ul className="divide-y divide-gray-100 dark:divide-zinc-700">
          {receipts.map((r) => (
            <ReceiptRow
              key={r.id}
              receipt={r}
              attention={attention.itemsByReceiptId.get(r.id) ?? null}
              onDelete={handleDelete}
              onReview={() => setReviewing(r)}
              working={working}
            />
          ))}
        </ul>
      )}

      {reviewing ? (
        <ManufacturingReceiptExtractionModal
          receipt={reviewing}
          materials={materials}
          onClose={() => setReviewing(null)}
          onReceiptMutated={() => {
            void refresh();
            void attention.refresh();
          }}
          onMaterialCreated={() => void refreshMaterials()}
        />
      ) : null}

      {total > pageSize ? (
        <div className="flex items-center justify-end gap-1 border-t border-gray-200 pt-2 dark:border-zinc-700">
          <button
            type="button"
            disabled={!canPrev}
            onClick={() => setPage(safePage - 1)}
            className="rounded-md border border-gray-200 bg-white px-2 py-0.5 text-[11px] font-medium text-gray-800 disabled:opacity-40 dark:border-zinc-600 dark:bg-zinc-900 dark:text-zinc-100"
          >
            Prev
          </button>
          <span className="px-1 text-[11px] tabular-nums text-gray-600 dark:text-zinc-400">
            {safePage + 1} / {pageCount}
          </span>
          <button
            type="button"
            disabled={!canNext}
            onClick={() => setPage(safePage + 1)}
            className="rounded-md border border-gray-200 bg-white px-2 py-0.5 text-[11px] font-medium text-gray-800 disabled:opacity-40 dark:border-zinc-600 dark:bg-zinc-900 dark:text-zinc-100"
          >
            Next
          </button>
        </div>
      ) : null}
    </div>
  );
}

function ReceiptRow(props: {
  receipt: MfgInventoryReceipt;
  attention: MfgReceiptAttentionItem | null;
  onDelete: (id: string) => void;
  onReview: () => void;
  working: boolean;
}) {
  const { receipt: r, attention, onDelete, onReview, working } = props;
  const total = Number(r.total_cost) || 0;
  const ship = Number(r.shipping_cost) || 0;
  const tax = Number(r.tax_amount) || 0;
  return (
    <li className="flex items-start justify-between gap-2 py-2.5">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-gray-900 dark:text-zinc-100">
            {r.vendor_name || "(no vendor)"}
          </span>
          {r.plant_code ? (
            <span className="text-[11px] text-gray-500 dark:text-zinc-400">{r.plant_code}</span>
          ) : null}
        </div>
        <div className="mt-0.5 text-xs text-gray-700 dark:text-zinc-300">
          <span className="tabular-nums font-medium">${total.toFixed(2)}</span>
          {ship > 0 ? <span className="text-gray-500 dark:text-zinc-400"> · ship ${ship.toFixed(2)}</span> : null}
          {tax > 0 ? <span className="text-gray-500 dark:text-zinc-400"> · tax ${tax.toFixed(2)}</span> : null}
          {r.uploaded_by_label ? (
            <span className="text-gray-500 dark:text-zinc-400"> · by {r.uploaded_by_label}</span>
          ) : null}
        </div>
        {r.notes ? (
          <div className="mt-0.5 truncate text-[11px] text-gray-500 dark:text-zinc-400">{r.notes}</div>
        ) : null}
      </div>
      <div className="flex shrink-0 flex-col items-end gap-1">
        <span className="text-[11px] text-gray-500 dark:text-zinc-500">{r.receipt_date}</span>
        <ManufacturingReceiptStatusChip receipt={r} attention={attention} />
        <div className="flex items-center gap-1">
          {r.view_url || r.public_url ? (
            <a
              href={(r.view_url || r.public_url) as string}
              target="_blank"
              rel="noreferrer"
              className="rounded-md border border-gray-200 bg-white px-2 py-0.5 text-[11px] font-medium text-indigo-700 hover:bg-indigo-50 dark:border-zinc-600 dark:bg-zinc-900 dark:text-indigo-300 dark:hover:bg-zinc-800"
            >
              Open
            </a>
          ) : null}
          <button
            type="button"
            onClick={onReview}
            disabled={working}
            className="rounded-md border border-indigo-300 bg-indigo-600 px-2 py-0.5 text-[11px] font-medium text-white hover:bg-indigo-700 disabled:opacity-40"
          >
            Review
          </button>
          <button
            type="button"
            onClick={() => onDelete(r.id)}
            disabled={working}
            className="rounded-md border border-red-200 bg-white px-2 py-0.5 text-[11px] font-medium text-red-700 hover:bg-red-50 disabled:opacity-40 dark:border-red-800 dark:bg-zinc-900 dark:text-red-300 dark:hover:bg-red-950/40"
          >
            Delete
          </button>
        </div>
      </div>
    </li>
  );
}

